import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";

import Sidebar from "../DesignSystem/Sidebar/Sidebar";
import AMLDateInput from "../Components/Inputs/DateInput";
import { db } from "../../backend/firebaseConfig";
import { useUserRole } from "../../context/UserRoleContext";
import { formatCheckType } from "../CheckDetails/Utils/formatCheckType";
import { computeOverallOutcome } from "../CheckDetails/Utils/outcome/checkOutcome";
import { getAllMatchesResolved } from "../CheckDetails/Utils/Experian/allMatchesHaveReasons";
import { formatGBP, getCheckPricing } from "./Utils/checkPricing";
import PrimaryButton from "../Components/Buttons/PrimaryButton";

import "../../css/AMLDasboard.css";
import "../../css/Pages/Statement.css";

const FALLBACK_COMPANY = "Unassigned Company";
const FETCH_LIMIT = 2500;
const DEBUG_STATEMENT = true;

const normalise = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const idsParam = (arr) => arr.map(encodeURIComponent).join(",");

const classifyBucket = (d = {}) => {
  const isTest =
    d.isTest === true ||
    normalise(d.environment) === "test" ||
    normalise(d.mode) === "test" ||
    normalise(d.checkType) === "test";
  if (isTest) return "test";

  const ct = normalise(d.checkType);

  if (ct === "enhanced" || ct === "idv" || ct === "identity verification") {
    return "idv";
  }

  if (ct === "standard" || ct === "document only" || ct === "document") {
    return "doconly";
  }

  if (ct === "simple") return "dbWs";

  const docOnlyHint =
    d.docOnly === true ||
    normalise(d.flow) === "document_only" ||
    normalise(d.veriffFlow) === "document_only";

  if (docOnlyHint) return "doconly";

  return "idv";
};

const getCreatedDate = (createdAt) => {
  if (!createdAt) return null;
  if (typeof createdAt?.toDate === "function") return createdAt.toDate();
  const date = new Date(createdAt);
  return Number.isNaN(date.getTime()) ? null : date;
};

const buildClientName = (requestData = {}, decision = {}, fallback = "") => {
  const reqFirst =
    requestData?.personal?.firstName ||
    requestData?.personalDetails?.firstName ||
    "";
  const reqLast =
    requestData?.personal?.lastName ||
    requestData?.personalDetails?.lastName ||
    "";

  const decFirst = decision?.verification?.person?.firstName || "";
  const decLast = decision?.verification?.person?.lastName || "";

  return (
    `${reqFirst || decFirst} ${reqLast || decLast}`.trim() ||
    fallback ||
    "Unknown Client"
  );
};

const pickCompany = (companyByEmail, ownerEmail, createdBy, forAdviser) => {
  const fallbackEmail =
    String(ownerEmail || "").trim() ||
    String(createdBy || "").trim() ||
    String(forAdviser || "").trim();

  return (
    companyByEmail.get(normalise(ownerEmail)) ||
    companyByEmail.get(normalise(createdBy)) ||
    companyByEmail.get(normalise(forAdviser)) ||
    fallbackEmail ||
    FALLBACK_COMPANY
  );
};

function monthBounds(monthValue) {
  const m = dayjs(`${monthValue}-01`);
  return {
    start: m.startOf("month").toDate(),
    end: m.endOf("month").add(1, "millisecond").toDate(),
  };
}

function lastTenDaysBounds(monthValue) {
  const end = dayjs(`${monthValue}-01`).endOf("month");
  const start = end.subtract(9, "day").startOf("day");
  return {
    start: start.toDate(),
    end: end.endOf("day").toDate(),
  };
}

function monthValueToDisplay(monthValue) {
  const parsed = dayjs(`${monthValue}-01`, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.format("MM/YYYY") : "";
}

function monthDisplayToValue(displayValue) {
  const parsed = dayjs(displayValue, "MM/YYYY", true);
  return parsed.isValid() ? parsed.format("YYYY-MM") : "";
}

async function fetchRequestDataForDocs(docs) {
  const tasks = docs.map(async ({ id: checkId, data, ownerEmail }) => {
    const basePath = `AML Checks/${ownerEmail}/Checks/${checkId}/Request Data`;
    const explicitId = data?.requestDataId;

    try {
      if (explicitId) {
        const reqRef = doc(db, basePath, explicitId);
        const reqSnap = await getDoc(reqRef);
        return [checkId, reqSnap.exists() ? reqSnap.data() : null];
      }

      const reqSnap = await getDocs(collection(db, basePath));
      return [checkId, reqSnap.docs[0] ? reqSnap.docs[0].data() : null];
    } catch {
      return [checkId, null];
    }
  });

  const entries = await Promise.all(tasks);
  return new Map(entries);
}

async function fetchExperianDataForDocs(docs) {
  const tasks = docs.map(async ({ id: checkId, ownerEmail }) => {
    const basePath = `AML Checks/${ownerEmail}/Checks/${checkId}/Experian Response`;

    try {
      const expSnap = await getDocs(collection(db, basePath));
      const first = expSnap.docs[0]?.data() || null;
      return [
        checkId,
        { experianResponse: first, reasonsByRef: first?.reasonsByRef || {} },
      ];
    } catch {
      return [checkId, { experianResponse: null, reasonsByRef: {} }];
    }
  });

  const entries = await Promise.all(tasks);
  return new Map(entries);
}

async function fetchDecisionRows(apiBase, sessionIds, endpoint) {
  if (!apiBase || sessionIds.length === 0) return [];

  const chunkSize = 80;
  const chunks = [];
  for (let i = 0; i < sessionIds.length; i += chunkSize) {
    chunks.push(sessionIds.slice(i, i + chunkSize));
  }

  const requests = chunks.map(async (chunk) => {
    const res = await fetch(`${apiBase}${endpoint}?ids=${idsParam(chunk)}`);
    if (!res.ok) return [];
    const payload = await res.json();
    return Array.isArray(payload?.rows) ? payload.rows : [];
  });

  const all = await Promise.all(requests);
  return all.flat();
}

const toCsvValue = (value) => {
  const str = String(value ?? "");
  if (str.includes(",") || str.includes("\n") || str.includes('"')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const downloadCsv = (rows, month) => {
  const headers = [
    "Company",
    "User",
    "Check Type",
    "Client Name",
    "Date",
    "Decision Type",
    "Experian Response Available",
    "Base Charge Amount",
    "Experian Charge Amount",
    "Veriff Charge Amount",
    "Total Charge Amount",
  ];

  const lines = [headers.join(",")];
  rows.forEach((row) => {
    lines.push(
      [
        row.company,
        row.user,
        row.checkType,
        row.clientName,
        row.date,
        row.decisionType,
        row.hasExperianResponse ? "Yes" : "No",
        row.baseChargeAmount,
        row.experianChargeAmount,
        row.veriffChargeAmount,
        row.totalChargeAmount,
      ]
        .map(toCsvValue)
        .join(","),
    );
  });

  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `statement-${month}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

export default function Statement() {
  const { userRole } = useUserRole();
  const navigate = useNavigate();

  const [month, setMonth] = useState(dayjs().format("YYYY-MM"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedCompanies, setExpandedCompanies] = useState({});

  const monthDisplayValue = useMemo(() => monthValueToDisplay(month), [month]);

  useEffect(() => {
    if (userRole && userRole !== "superadmin") {
      if (DEBUG_STATEMENT) {
        console.log("[Statement] Redirecting non-superadmin user", {
          userRole,
        });
      }
      navigate("/my-checks", { replace: true });
    }
  }, [userRole, navigate]);

  useEffect(() => {
    if (userRole !== "superadmin") {
      if (DEBUG_STATEMENT) {
        console.log("[Statement] Skipping load: role is not superadmin", {
          userRole,
        });
      }
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const { start, end } = monthBounds(month);
        const { start: lastTenStart, end: lastTenEnd } =
          lastTenDaysBounds(month);

        if (DEBUG_STATEMENT) {
          console.log("[Statement] Loading month", {
            month,
            start: start.toISOString(),
            end: end.toISOString(),
            lastTenStart: lastTenStart.toISOString(),
            lastTenEnd: lastTenEnd.toISOString(),
            fetchLimit: FETCH_LIMIT,
          });
        }

        const checksSnap = await getDocs(
          query(
            collectionGroup(db, "Checks"),
            orderBy("createdAt", "desc"),
            limit(FETCH_LIMIT),
          ),
        );

        if (DEBUG_STATEMENT) {
          console.log("[Statement] Raw checks fetched", {
            count: checksSnap.size,
          });
        }

        const baseChecks = checksSnap.docs
          .map((snap) => ({
            id: snap.id,
            data: snap.data(),
            ownerEmail: snap.ref?.parent?.parent?.id || "",
          }))
          .filter(({ data }) => {
            const createdDate = getCreatedDate(data?.createdAt);
            if (!createdDate) return false;
            return createdDate >= start && createdDate < end;
          });

        if (DEBUG_STATEMENT) {
          const samples = checksSnap.docs.slice(0, 3).map((d) => {
            const raw = d.data()?.createdAt;
            const parsed = getCreatedDate(raw);
            return {
              id: d.id,
              rawCreatedAt: raw,
              parsedCreatedAt: parsed ? parsed.toISOString() : null,
            };
          });

          console.log("[Statement] Checks after month filter", {
            month,
            filteredCount: baseChecks.length,
            samples,
          });
        }

        const userSnap = await getDocs(collection(db, "AML Checks"));
        const companyByEmail = new Map();
        userSnap.forEach((docSnap) => {
          const company =
            String(
              docSnap.data()?.company ||
                docSnap.data()?.companyName ||
                docSnap.data()?.brandName ||
                "",
            ).trim() || FALLBACK_COMPANY;
          companyByEmail.set(normalise(docSnap.id), company);
          const email = normalise(docSnap.data()?.email);
          if (email) companyByEmail.set(email, company);
        });

        const [requestDataMap, experianMap] = await Promise.all([
          fetchRequestDataForDocs(baseChecks),
          fetchExperianDataForDocs(baseChecks),
        ]);

        const idvIds = [];
        const docOnlyIds = [];

        baseChecks.forEach(({ data }) => {
          const sessionId = data?.sessionId;
          if (!sessionId) return;
          const bucket = classifyBucket(data);
          if (bucket === "idv") idvIds.push(sessionId);
          if (bucket === "doconly") docOnlyIds.push(sessionId);
        });

        const apiBase = process.env.REACT_APP_AML_SERVER;
        const [idvRows, docOnlyRows] = await Promise.all([
          fetchDecisionRows(apiBase, idvIds, "get-idv-decisions/"),
          fetchDecisionRows(apiBase, docOnlyIds, "get-docOnly-decisions/"),
        ]);

        if (DEBUG_STATEMENT) {
          console.log("[Statement] Decision enrichment", {
            idvSessionCount: idvIds.length,
            docOnlySessionCount: docOnlyIds.length,
            idvRows: idvRows.length,
            docOnlyRows: docOnlyRows.length,
          });
        }

        const decisionBySessionId = new Map(
          [...idvRows, ...docOnlyRows].map((row) => [
            row.sessionId || row.id,
            row,
          ]),
        );

        const computedRows = baseChecks.map(({ id, data, ownerEmail }) => {
          const requestData = requestDataMap.get(id) || {};
          const expPack = experianMap.get(id) || {
            experianResponse: null,
            reasonsByRef: {},
          };

          const enriched = decisionBySessionId.get(data?.sessionId) || {};

          const rowOrCheck = {
            ...data,
            ...enriched,
            checkType: data?.checkType || enriched?.checkType || "",
          };

          const { checkResult } = computeOverallOutcome({
            rowOrCheck,
            checkType: rowOrCheck.checkType,
            experianResponse: expPack.experianResponse,
            reasonsByRef: expPack.reasonsByRef,
            getAllMatchesResolved,
          });

          const checkTypeLower = normalise(rowOrCheck.checkType);
          const decisionType = normalise(checkResult);
          const hasExperianResponse = Boolean(expPack.experianResponse);
          const hasVeriffSession = Boolean(
            rowOrCheck?.sessionId || data?.sessionId,
          );
          const pricing = getCheckPricing(rowOrCheck.checkType);
          const createdDate = getCreatedDate(data?.createdAt);
          const isApprovedOrDeclined =
            decisionType === "approved" || decisionType === "declined";
          const isInLastTenDaysOfMonth =
            createdDate &&
            createdDate >= lastTenStart &&
            createdDate <= lastTenEnd;
          const veriffChargeEligible =
            (checkTypeLower === "enhanced" || checkTypeLower === "standard") &&
            hasVeriffSession &&
            isApprovedOrDeclined &&
            isInLastTenDaysOfMonth;

          const baseChargeAmount = pricing.net;
          const experianChargeAmount = hasExperianResponse
            ? pricing.experian
            : 0;
          const veriffChargeAmount = veriffChargeEligible ? pricing.veriff : 0;
          const totalChargeAmount =
            baseChargeAmount + experianChargeAmount + veriffChargeAmount;

          return {
            id,
            company: pickCompany(
              companyByEmail,
              ownerEmail,
              data?.createdBy,
              data?.forAdviser,
            ),
            user: data?.createdBy || ownerEmail || "Unknown",
            ownerEmail,
            checkType:
              formatCheckType(rowOrCheck.checkType || "") ||
              rowOrCheck.checkType ||
              "Unknown",
            clientName: buildClientName(
              requestData,
              enriched?.decision,
              data?.name,
            ),
            date: createdDate ? dayjs(createdDate).format("DD/MM/YYYY") : "-",
            decisionType: checkResult || "Processing",
            hasExperianResponse,
            hasVeriffSession,
            isInLastTenDaysOfMonth,
            baseChargeAmount,
            experianChargeAmount,
            veriffChargeAmount,
            totalChargeAmount,
          };
        });

        if (!cancelled) {
          if (DEBUG_STATEMENT) {
            console.log("[Statement] Final computed rows", {
              month,
              count: computedRows.length,
            });
          }
          setRows(computedRows);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("[Statement] Failed to load statement", e);
          setError(e?.message || "Failed to load statement");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [month, userRole]);

  const groupedByCompany = useMemo(() => {
    const map = new Map();

    rows.forEach((row) => {
      if (!map.has(row.company)) {
        map.set(row.company, []);
      }
      map.get(row.company).push(row);
    });

    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  const summary = useMemo(() => {
    const totalChecks = rows.length;
    const totalBase = rows.reduce(
      (sum, row) => sum + Number(row.baseChargeAmount || 0),
      0,
    );
    const totalExperian = rows.reduce(
      (sum, row) => sum + Number(row.experianChargeAmount || 0),
      0,
    );
    const totalVeriff = rows.reduce(
      (sum, row) => sum + Number(row.veriffChargeAmount || 0),
      0,
    );

    return {
      totalChecks,
      totalBase,
      totalExperian,
      totalVeriff,
      totalChargeAmount: totalBase + totalExperian + totalVeriff,
    };
  }, [rows]);

  const toggleCompany = (company) => {
    setExpandedCompanies((prev) => ({
      ...prev,
      [company]: !prev[company],
    }));
  };

  const downloadCompanyCsv = (companyName, companyRows) => {
    const safe = String(companyName || "company")
      .replace(/[^a-z0-9-_ ]/gi, "")
      .trim()
      .replace(/\s+/g, "-")
      .toLowerCase();

    const fileMonth = month || dayjs().format("YYYY-MM");
    downloadCsv(companyRows, `${fileMonth}-${safe || "company"}`);
  };

  if (userRole !== "superadmin") {
    navigate("/my-checks", { replace: true });
    return null;
  }

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Statement</title>
      </Helmet>

      <Sidebar />

      <div className="statement-container">
        <div className="statement-header">
          <h1 className="AML-Text-Title primary">Monthly Statement</h1>

          <div className="statement-actions">
            <div className="statement-month-picker">
              <AMLDateInput
                label="Month"
                value={monthDisplayValue}
                onChange={(next) => {
                  const mapped = monthDisplayToValue(next);
                  if (mapped) setMonth(mapped);
                }}
                dateFormat="MM/YYYY"
              />
            </div>

            <PrimaryButton
              type="button"
              onClick={() => downloadCsv(rows, month)}
              disabled={rows.length === 0}
              label="Export All to Excel (CSV)"
            >
              {/* Export to Excel (CSV) */}
            </PrimaryButton>
          </div>
        </div>

        <div className="statement-summary-grid">
          <div className="statement-summary-card">
            <span className="statement-summary-label AML-Text-Body">
              Checks
            </span>
            <strong className="AML-Text-Input">{summary.totalChecks}</strong>
          </div>
          <div className="statement-summary-card">
            <span className="statement-summary-label AML-Text-Body">
              Base Charges
            </span>
            <strong className="AML-Text-Input">
              {formatGBP(summary.totalBase)}
            </strong>
          </div>
          <div className="statement-summary-card">
            <span className="statement-summary-label AML-Text-Body">
              Experian Charges
            </span>
            <strong className="AML-Text-Input">
              {formatGBP(summary.totalExperian)}
            </strong>
          </div>
          <div className="statement-summary-card">
            <span className="statement-summary-label AML-Text-Body">
              Veriff Charges
            </span>
            <strong className="AML-Text-Input">
              {formatGBP(summary.totalVeriff)}
            </strong>
          </div>
          <div className="statement-summary-card">
            <span className="statement-summary-label AML-Text-Body">
              Total Charges
            </span>
            <strong className="AML-Text-Input">
              {formatGBP(summary.totalChargeAmount)}
            </strong>
          </div>
        </div>

        {loading ? (
          <div className="statement-empty AML-Text-Body">
            Loading statement...
          </div>
        ) : null}
        {!loading && error ? (
          <div className="statement-error AML-Text-Body">{error}</div>
        ) : null}
        {!loading && !error && rows.length === 0 ? (
          <div className="statement-empty AML-Text-Body">
            No checks found for this month.
          </div>
        ) : null}

        {!loading && !error
          ? groupedByCompany.map(([company, companyRows]) => {
              const companyTotal = companyRows.reduce(
                (sum, row) => sum + Number(row.totalChargeAmount || 0),
                0,
              );
              const companyVeriff = companyRows.reduce(
                (sum, row) => sum + Number(row.veriffChargeAmount || 0),
                0,
              );
              const companyExperian = companyRows.reduce(
                (sum, row) => sum + Number(row.experianChargeAmount || 0),
                0,
              );
              const isExpanded = Boolean(expandedCompanies[company]);

              return (
                <section key={company} className="statement-company-section">
                  <div className="statement-company-card">
                    <div
                      className="statement-company-header statement-company-header-clickable"
                      onClick={() => toggleCompany(company)}
                    >
                      <h2 className="AML-Text-Subtitle">{company}</h2>
                      <span className="AML-Text-Body">
                        {companyRows.length} checks · Experian · {formatGBP(companyExperian)} · Veriff · {formatGBP(companyVeriff)} · Total · {formatGBP(companyTotal)} {isExpanded ? "▴" : "▾"}
                      </span>
                    </div>

                    {isExpanded ? (
                      <div className="statement-company-actions">
                        <div className="statement-company-breakdown">
                          <span className="AML-Text-Body">
                            Experian: {formatGBP(companyExperian)}
                          </span>
                          <span className="AML-Text-Body">
                            Veriff: {formatGBP(companyVeriff)}
                          </span>
                          <span className="AML-Text-Body">
                            Total: {formatGBP(companyTotal)}
                          </span>
                        </div>

                        <PrimaryButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadCompanyCsv(company, companyRows);
                          }}
                          label={`Export Excel`}
                        >
                          {/* Export {company} Excel (CSV) */}
                        </PrimaryButton>
                      </div>
                    ) : null}
                  </div>
                </section>
              );
            })
          : null}
      </div>
      {/* <main className="statement-main">
      </main> */}
    </div>
  );
}
